import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  View, Text, StyleSheet, ScrollView, TouchableOpacity,
  TextInput, Alert, ActivityIndicator, Platform
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Picker } from '@react-native-picker/picker';
import DateTimePicker from '@react-native-community/datetimepicker';
import { useAuth } from '../../contexts/AuthContext';
import SignaturePad from '../../components/SignaturePad';
import api from '../../services/api';

const formatCurrency = (val) => `L. ${parseFloat(val || 0).toFixed(2)}`;

const formatDateYMD = (d) => {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const GenerarVoucherScreen = ({ navigation }) => {
  const { user } = useAuth();
  const firmaRef = useRef(null);

  const [empleados, setEmpleados] = useState([]);
  const [selectedEmpleado, setSelectedEmpleado] = useState(null);
  const [datosEmpleado, setDatosEmpleado] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingDatos, setLoadingDatos] = useState(false);
  const [saving, setSaving] = useState(false);

  // Campos del voucher
  const [periodoInicio, setPeriodoInicio] = useState('');
  const [periodoFin, setPeriodoFin] = useState('');
  const [salarioBase, setSalarioBase] = useState('0');
  const [otrosIngresos, setOtrosIngresos] = useState('0');
  const [otrasDeducciones, setOtrasDeducciones] = useState('0');
  const [metodoPago, setMetodoPago] = useState('efectivo');
  const [referenciaPago, setReferenciaPago] = useState('');
  const [notas, setNotas] = useState('');
  const [firmaEmisor, setFirmaEmisor] = useState(null);
  const [detalles, setDetalles] = useState([]);
  const [showFirma, setShowFirma] = useState(false);
  const [firmando, setFirmando] = useState(false);
  const [showPickerInicio, setShowPickerInicio] = useState(false);
  const [showPickerFin, setShowPickerFin] = useState(false);

  useEffect(() => {
    cargarEmpleados();
    // Establecer período por defecto (mes actual)
    const now = new Date();
    const inicio = new Date(now.getFullYear(), now.getMonth(), 1);
    const fin = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    setPeriodoInicio(inicio.toISOString().split('T')[0]);
    setPeriodoFin(fin.toISOString().split('T')[0]);
  }, []);

  const cargarEmpleados = async () => {
    setLoading(true);
    try {
      const data = await api.getVoucherEmpleados();
      setEmpleados(data);
    } catch (error) {
      console.error('Error al cargar empleados:', error);
    } finally {
      setLoading(false);
    }
  };

  const cargarDatosEmpleado = async (empleadoId) => {
    if (!empleadoId) return;
    setLoadingDatos(true);
    try {
      const data = await api.getVoucherDatosEmpleado(empleadoId);

      // Normalizar la respuesta — si el endpoint no devuelve algún bloque,
      // dejamos arrays/objetos vacíos para que la UI no crashee.
      const safeData = {
        empleado: data?.empleado || null,
        comisiones: Array.isArray(data?.comisiones) ? data.comisiones : [],
        pagos_comisiones: Array.isArray(data?.pagos_comisiones) ? data.pagos_comisiones : [],
        penalizaciones: Array.isArray(data?.penalizaciones) ? data.penalizaciones : [],
        resumen: {
          total_comisiones: Number(data?.resumen?.total_comisiones) || 0,
          total_bonos: Number(data?.resumen?.total_bonos) || 0,
          total_penalizaciones: Number(data?.resumen?.total_penalizaciones) || 0,
          neto_estimado: Number(data?.resumen?.neto_estimado) || 0,
        },
      };
      setDatosEmpleado(safeData);

      // Auto-generar detalles del voucher.
      // Las comisiones se incluyen una a una con referencia_id para que al
      // pagar el voucher el backend las marque como 'pagada' y no se reciclen
      // en el siguiente voucher del empleado.
      const nuevosDetalles = [];

      const comisionesAprobadas = safeData.comisiones.filter(c => c.estado === 'aprobada');
      for (const com of comisionesAprobadas) {
        nuevosDetalles.push({
          concepto: `Comisión venta #${com.venta_id}${com.cliente_nombre ? ' - ' + com.cliente_nombre : ''}`,
          tipo: 'ingreso',
          categoria: 'comision',
          referencia_id: com.id,
          referencia_tipo: 'comision',
          monto: parseFloat(com.monto_total) || 0,
        });
      }

      const bonos = safeData.penalizaciones.filter(p => p.tipo === 'bono');
      for (const bono of bonos) {
        nuevosDetalles.push({
          concepto: `Bono: ${bono.motivo}`,
          tipo: 'ingreso',
          categoria: 'bono',
          referencia_id: bono.id,
          referencia_tipo: 'penalizacion',
          monto: parseFloat(bono.monto) || 0,
        });
      }

      const pens = safeData.penalizaciones.filter(p => p.tipo === 'penalizacion');
      for (const pen of pens) {
        nuevosDetalles.push({
          concepto: `Penalización: ${pen.motivo}`,
          tipo: 'deduccion',
          categoria: 'penalizacion',
          referencia_id: pen.id,
          referencia_tipo: 'penalizacion',
          monto: parseFloat(pen.monto) || 0,
        });
      }

      setDetalles(nuevosDetalles);
    } catch (error) {
      console.error('Error al cargar datos del empleado:', error);
      Alert.alert(
        'Error al cargar datos',
        error?.message || 'No se pudieron cargar los datos del empleado. Verificá tu permiso para este módulo.'
      );
    } finally {
      setLoadingDatos(false);
    }
  };

  const handleEmpleadoChange = (empleadoId) => {
    setSelectedEmpleado(empleadoId);
    setDatosEmpleado(null);
    setDetalles([]);
    if (empleadoId) {
      cargarDatosEmpleado(empleadoId);
    }
  };

  const calcularTotales = () => {
    const sBase = parseFloat(salarioBase) || 0;
    const tComisiones = datosEmpleado?.resumen?.total_comisiones || 0;
    const tBonos = datosEmpleado?.resumen?.total_bonos || 0;
    const tPenalizaciones = datosEmpleado?.resumen?.total_penalizaciones || 0;
    const oIng = parseFloat(otrosIngresos) || 0;
    const oDed = parseFloat(otrasDeducciones) || 0;

    const totalIngresos = sBase + tComisiones + tBonos + oIng;
    const totalDeducciones = tPenalizaciones + oDed;
    const neto = totalIngresos - totalDeducciones;

    return { totalIngresos, totalDeducciones, neto, sBase, tComisiones, tBonos, tPenalizaciones, oIng, oDed };
  };

  const handleFirmaCapturada = useCallback((data) => {
    setFirmaEmisor(data);
  }, []);

  const handleGuardar = async () => {
    if (!selectedEmpleado) {
      return Alert.alert('Error', 'Selecciona un empleado');
    }
    if (!periodoInicio || !periodoFin) {
      return Alert.alert('Error', 'El período es requerido');
    }

    const totales = calcularTotales();

    setSaving(true);
    try {
      // Agregar detalles manuales si hay salario base
      const allDetalles = [...detalles];
      if (totales.sBase > 0) {
        allDetalles.unshift({
          concepto: 'Salario base',
          tipo: 'ingreso',
          categoria: 'salario',
          monto: totales.sBase,
        });
      }
      if (totales.oIng > 0) {
        allDetalles.push({
          concepto: 'Otros ingresos',
          tipo: 'ingreso',
          categoria: 'otro',
          monto: totales.oIng,
        });
      }
      if (totales.oDed > 0) {
        allDetalles.push({
          concepto: 'Otras deducciones',
          tipo: 'deduccion',
          categoria: 'otro',
          monto: totales.oDed,
        });
      }

      const voucherData = {
        empleado_id: selectedEmpleado,
        periodo_inicio: periodoInicio,
        periodo_fin: periodoFin,
        salario_base: totales.sBase,
        total_comisiones: totales.tComisiones,
        total_bonos: totales.tBonos,
        total_penalizaciones: totales.tPenalizaciones,
        otros_ingresos: totales.oIng,
        otras_deducciones: totales.oDed,
        metodo_pago: metodoPago,
        referencia_pago: referenciaPago || null,
        notas: notas || null,
        firma_emisor: firmaEmisor,
        detalles: allDetalles,
      };

      const result = await api.createVoucher(voucherData);

      Alert.alert(
        'Voucher Creado',
        `Voucher ${result.numero_voucher} generado exitosamente.\nMonto neto: ${formatCurrency(result.monto_neto)}`,
        [{ text: 'Ver Voucher', onPress: () => navigation.replace('DetalleVoucher', { voucherId: result.id }) }]
      );
    } catch (error) {
      console.error('Error al crear voucher:', error);
      Alert.alert('Error', error.message || 'No se pudo crear el voucher');
    } finally {
      setSaving(false);
    }
  };

  const totales = calcularTotales();

  if (loading) {
    return (
      <View style={styles.centered}>
        <ActivityIndicator size="large" color="#007bff" />
      </View>
    );
  }

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.scrollContent}
      keyboardShouldPersistTaps="handled"
      keyboardDismissMode="on-drag"
      scrollEnabled={!firmando}
    >
      {/* Selección de empleado */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>
          <Ionicons name="person-outline" size={16} /> Empleado
        </Text>
        <View style={styles.pickerContainer}>
          <Picker
            selectedValue={selectedEmpleado}
            onValueChange={handleEmpleadoChange}
            style={styles.picker}
          >
            <Picker.Item label="Seleccionar empleado..." value={null} />
            {empleados.map(emp => (
              <Picker.Item
                key={emp.id}
                label={`${emp.nombre} (${emp.rol})`}
                value={emp.id}
              />
            ))}
          </Picker>
        </View>
      </View>

      {/* Período */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>
          <Ionicons name="calendar-outline" size={16} /> Período
        </Text>
        <View style={styles.row}>
          <View style={styles.inputHalf}>
            <Text style={styles.label}>Desde</Text>
            <TouchableOpacity
              style={styles.dateBtn}
              onPress={() => setShowPickerInicio(true)}
              activeOpacity={0.7}
            >
              <Ionicons name="calendar-outline" size={16} color="#0066cc" />
              <Text style={[styles.dateBtnText, !periodoInicio && styles.dateBtnPlaceholder]}>
                {periodoInicio || 'YYYY-MM-DD'}
              </Text>
            </TouchableOpacity>
            {showPickerInicio && (
              <DateTimePicker
                value={periodoInicio ? new Date(periodoInicio) : new Date()}
                mode="date"
                display={Platform.OS === 'ios' ? 'inline' : 'default'}
                onChange={(event, selectedDate) => {
                  setShowPickerInicio(Platform.OS === 'ios');
                  if (selectedDate) setPeriodoInicio(formatDateYMD(selectedDate));
                }}
              />
            )}
          </View>
          <View style={styles.inputHalf}>
            <Text style={styles.label}>Hasta</Text>
            <TouchableOpacity
              style={styles.dateBtn}
              onPress={() => setShowPickerFin(true)}
              activeOpacity={0.7}
            >
              <Ionicons name="calendar-outline" size={16} color="#0066cc" />
              <Text style={[styles.dateBtnText, !periodoFin && styles.dateBtnPlaceholder]}>
                {periodoFin || 'YYYY-MM-DD'}
              </Text>
            </TouchableOpacity>
            {showPickerFin && (
              <DateTimePicker
                value={periodoFin ? new Date(periodoFin) : new Date()}
                mode="date"
                display={Platform.OS === 'ios' ? 'inline' : 'default'}
                minimumDate={periodoInicio ? new Date(periodoInicio) : undefined}
                onChange={(event, selectedDate) => {
                  setShowPickerFin(Platform.OS === 'ios');
                  if (selectedDate) setPeriodoFin(formatDateYMD(selectedDate));
                }}
              />
            )}
          </View>
        </View>
      </View>

      {/* Datos del empleado (cargados automáticamente) */}
      {loadingDatos && (
        <View style={styles.loadingSection}>
          <ActivityIndicator size="small" color="#007bff" />
          <Text style={styles.loadingText}>Cargando datos del empleado...</Text>
        </View>
      )}

      {datosEmpleado && (
        <>
          {/* Resumen automático */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>
              <Ionicons name="stats-chart-outline" size={16} /> Datos Automáticos
            </Text>

            <View style={styles.autoRow}>
              <View style={[styles.autoCard, { borderLeftColor: '#28a745' }]}>
                <Text style={styles.autoLabel}>Comisiones</Text>
                <Text style={[styles.autoValue, { color: '#28a745' }]}>
                  {formatCurrency(datosEmpleado.resumen.total_comisiones)}
                </Text>
                <Text style={styles.autoDetail}>
                  {datosEmpleado.comisiones.filter(c => c.estado === 'aprobada').length} aprobadas
                </Text>
              </View>
              <View style={[styles.autoCard, { borderLeftColor: '#17a2b8' }]}>
                <Text style={styles.autoLabel}>Bonos</Text>
                <Text style={[styles.autoValue, { color: '#17a2b8' }]}>
                  {formatCurrency(datosEmpleado.resumen.total_bonos)}
                </Text>
              </View>
              <View style={[styles.autoCard, { borderLeftColor: '#dc3545' }]}>
                <Text style={styles.autoLabel}>Penalizaciones</Text>
                <Text style={[styles.autoValue, { color: '#dc3545' }]}>
                  {formatCurrency(datosEmpleado.resumen.total_penalizaciones)}
                </Text>
              </View>
            </View>
          </View>

          {/* Montos manuales */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>
              <Ionicons name="create-outline" size={16} /> Montos Adicionales
            </Text>
            <View style={styles.row}>
              <View style={styles.inputThird}>
                <Text style={styles.label}>Salario Base</Text>
                <TextInput
                  style={styles.input}
                  value={salarioBase}
                  onChangeText={setSalarioBase}
                  keyboardType="numeric"
                  placeholder="0.00"
                />
              </View>
              <View style={styles.inputThird}>
                <Text style={styles.label}>Otros Ingresos</Text>
                <TextInput
                  style={styles.input}
                  value={otrosIngresos}
                  onChangeText={setOtrosIngresos}
                  keyboardType="numeric"
                  placeholder="0.00"
                />
              </View>
              <View style={styles.inputThird}>
                <Text style={styles.label}>Otras Deducciones</Text>
                <TextInput
                  style={styles.input}
                  value={otrasDeducciones}
                  onChangeText={setOtrasDeducciones}
                  keyboardType="numeric"
                  placeholder="0.00"
                />
              </View>
            </View>
          </View>

          {/* Detalles generados */}
          {detalles.length > 0 && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>
                <Ionicons name="list-outline" size={16} /> Desglose del Voucher
              </Text>
              {detalles.map((det, idx) => (
                <View key={idx} style={styles.detalleRow}>
                  <Ionicons
                    name={det.tipo === 'ingreso' ? 'add-circle' : 'remove-circle'}
                    size={18}
                    color={det.tipo === 'ingreso' ? '#28a745' : '#dc3545'}
                  />
                  <Text style={styles.detalleConcepto} numberOfLines={1}>{det.concepto}</Text>
                  <Text style={[
                    styles.detalleMonto,
                    { color: det.tipo === 'ingreso' ? '#28a745' : '#dc3545' }
                  ]}>
                    {det.tipo === 'deduccion' ? '- ' : '+ '}{formatCurrency(det.monto)}
                  </Text>
                </View>
              ))}
            </View>
          )}

          {/* Totales */}
          <View style={styles.totalSection}>
            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>Total Ingresos</Text>
              <Text style={[styles.totalValue, { color: '#28a745' }]}>
                {formatCurrency(totales.totalIngresos)}
              </Text>
            </View>
            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>Total Deducciones</Text>
              <Text style={[styles.totalValue, { color: '#dc3545' }]}>
                - {formatCurrency(totales.totalDeducciones)}
              </Text>
            </View>
            <View style={[styles.totalRow, styles.totalRowFinal]}>
              <Text style={styles.totalLabelFinal}>MONTO NETO A PAGAR</Text>
              <Text style={styles.totalValueFinal}>{formatCurrency(totales.neto)}</Text>
            </View>
          </View>

          {/* Método de pago */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>
              <Ionicons name="wallet-outline" size={16} /> Método de Pago
            </Text>
            <View style={styles.pickerContainer}>
              <Picker
                selectedValue={metodoPago}
                onValueChange={setMetodoPago}
                style={styles.picker}
              >
                <Picker.Item label="Efectivo" value="efectivo" />
                <Picker.Item label="Transferencia bancaria" value="transferencia" />
                <Picker.Item label="Cheque" value="cheque" />
                <Picker.Item label="Depósito" value="deposito" />
              </Picker>
            </View>

            {metodoPago !== 'efectivo' && (
              <View style={{ marginTop: 10 }}>
                <Text style={styles.label}>Referencia / No. Transacción</Text>
                <TextInput
                  style={styles.input}
                  value={referenciaPago}
                  onChangeText={setReferenciaPago}
                  placeholder="Ej: TRF-123456"
                />
              </View>
            )}
          </View>

          {/* Notas */}
          <View style={styles.section}>
            <Text style={styles.label}>Notas (opcional)</Text>
            <TextInput
              style={[styles.input, { height: 60, textAlignVertical: 'top' }]}
              value={notas}
              onChangeText={setNotas}
              placeholder="Notas adicionales..."
              multiline
            />
          </View>

          {/* Firma del emisor */}
          <View style={styles.section}>
            <TouchableOpacity
              style={styles.firmaToggle}
              onPress={() => setShowFirma(!showFirma)}
            >
              <Ionicons name="finger-print-outline" size={20} color="#007bff" />
              <Text style={styles.firmaToggleText}>
                {showFirma ? 'Ocultar firma' : 'Agregar firma del emisor'}
              </Text>
              <Ionicons name={showFirma ? 'chevron-up' : 'chevron-down'} size={18} color="#007bff" />
            </TouchableOpacity>

            {showFirma && (
              <View style={styles.firmaContainer}>
                <SignaturePad
                  ref={firmaRef}
                  onSignature={handleFirmaCapturada}
                  onDrawingChange={setFirmando}
                  height={180}
                />
                <TouchableOpacity
                  style={styles.capturarFirmaBtn}
                  onPress={() => firmaRef.current?.getSignature()}
                >
                  <Ionicons name="checkmark-circle" size={18} color="#fff" />
                  <Text style={styles.capturarFirmaBtnText}>Capturar Firma</Text>
                </TouchableOpacity>
                {firmaEmisor && (
                  <View style={styles.firmaConfirmacion}>
                    <Ionicons name="checkmark-circle" size={16} color="#28a745" />
                    <Text style={styles.firmaConfirmacionText}>Firma capturada</Text>
                  </View>
                )}
              </View>
            )}
          </View>

          {/* Botón guardar */}
          <TouchableOpacity
            style={[styles.guardarBtn, saving && styles.guardarBtnDisabled]}
            onPress={handleGuardar}
            disabled={saving}
            activeOpacity={0.8}
          >
            {saving ? (
              <ActivityIndicator color="#fff" />
            ) : (
              <>
                <Ionicons name="receipt-outline" size={20} color="#fff" />
                <Text style={styles.guardarBtnText}>Generar Voucher</Text>
              </>
            )}
          </TouchableOpacity>
        </>
      )}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5' },
  scrollContent: { padding: 16, paddingBottom: 40 },
  centered: { flex: 1, justifyContent: 'center', alignItems: 'center' },

  section: {
    backgroundColor: '#fff', borderRadius: 10, padding: 16, marginBottom: 12,
    elevation: 1, shadowColor: '#000', shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05, shadowRadius: 2,
  },
  sectionTitle: { fontSize: 14, fontWeight: '700', color: '#333', marginBottom: 12 },

  pickerContainer: {
    borderWidth: 1, borderColor: '#ddd', borderRadius: 8, overflow: 'hidden',
    backgroundColor: '#fafafa',
  },
  picker: { height: 50 },

  row: { flexDirection: 'row', gap: 10 },
  inputHalf: { flex: 1 },
  inputThird: { flex: 1 },
  label: { fontSize: 12, color: '#666', marginBottom: 4, fontWeight: '500' },
  input: {
    borderWidth: 1, borderColor: '#ddd', borderRadius: 8, padding: 10,
    fontSize: 14, backgroundColor: '#fafafa', color: '#333',
  },
  dateBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    borderWidth: 1, borderColor: '#ddd', borderRadius: 8,
    padding: 10, backgroundColor: '#fafafa',
  },
  dateBtnText: { fontSize: 14, color: '#333', flex: 1 },
  dateBtnPlaceholder: { color: '#999' },

  loadingSection: { flexDirection: 'row', alignItems: 'center', gap: 8, padding: 20, justifyContent: 'center' },
  loadingText: { color: '#666', fontSize: 13 },

  autoRow: { flexDirection: 'row', gap: 8 },
  autoCard: {
    flex: 1, backgroundColor: '#fafafa', borderRadius: 8, padding: 10,
    borderLeftWidth: 3,
  },
  autoLabel: { fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 },
  autoValue: { fontSize: 14, fontWeight: '700', marginTop: 2 },
  autoDetail: { fontSize: 10, color: '#999', marginTop: 2 },

  detalleRow: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: '#f0f0f0',
  },
  detalleConcepto: { flex: 1, fontSize: 13, color: '#555' },
  detalleMonto: { fontSize: 14, fontWeight: '600' },

  totalSection: {
    backgroundColor: '#fff', borderRadius: 10, padding: 16, marginBottom: 12,
    elevation: 2,
  },
  totalRow: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    paddingVertical: 6,
  },
  totalLabel: { fontSize: 13, color: '#666' },
  totalValue: { fontSize: 15, fontWeight: '600' },
  totalRowFinal: {
    borderTopWidth: 2, borderTopColor: '#007bff', marginTop: 8, paddingTop: 12,
  },
  totalLabelFinal: { fontSize: 14, fontWeight: '700', color: '#333' },
  totalValueFinal: { fontSize: 20, fontWeight: '700', color: '#007bff' },

  firmaToggle: {
    flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 4,
  },
  firmaToggleText: { fontSize: 14, color: '#007bff', fontWeight: '500', flex: 1 },
  firmaContainer: { marginTop: 12 },
  capturarFirmaBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    gap: 6, backgroundColor: '#007bff', borderRadius: 8, padding: 10, marginTop: 10,
  },
  capturarFirmaBtnText: { color: '#fff', fontWeight: '600', fontSize: 14 },
  firmaConfirmacion: {
    flexDirection: 'row', alignItems: 'center', gap: 4, justifyContent: 'center',
    marginTop: 8,
  },
  firmaConfirmacionText: { color: '#28a745', fontSize: 13, fontWeight: '500' },

  guardarBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    gap: 8, backgroundColor: '#28a745', borderRadius: 12, padding: 16, marginTop: 8,
    elevation: 3,
  },
  guardarBtnDisabled: { opacity: 0.6 },
  guardarBtnText: { color: '#fff', fontSize: 16, fontWeight: '700' },
});

export default GenerarVoucherScreen;
